import { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  alpha,
  styled,
} from '@mui/material';
import { motion, useInView } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { P } from '../theme/palette';
import { useTenantConfig } from '../hooks/useTenant';

// SectionBadge pill component
const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  borderRadius: 50,
  background: alpha('#f97316', 0.08),
  border: `1px solid ${alpha('#f97316', 0.15)}`,
  marginBottom: 24,
});

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const FAQSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
    const { tenantName } = useTenantConfig(); 
  

  const faqs = [
    {
      question: `${tenantName} nedir?`,
      answer:
        `${tenantName}, derneklerin dijital dönüşümünü sağlayan bulut tabanlı bir yönetim platformudur. Üyelik, etkinlik, ödeme, iletişim ve web sitesi yönetimini tek panelde birleştirir.`,
    },
    {
      question: 'Ücretsiz deneme süresi var mı?',
      answer:
        'Evet! Tüm paketlerde <strong>7 gün ücretsiz deneme</strong> sunuyoruz. Kredi kartı gerekmez, hemen başlayabilirsiniz.',
    },
    {
      question: 'Ödeme yöntemleri neler?',
      answer:
        'Kredi kartı, banka havalesi ve EFT ile ödeme kabul ediyoruz. Yıllık ödemelerde %20 indirim uygulanır.',
    },
    {
      question: 'Verilerim güvende mi?',
      answer:
        'Kesinlikle. 256-bit SSL şifreleme, KVKK & GDPR uyumluluğu, ISO 27001 sertifikalı sunucular ve günlük otomatik yedekleme ile verileriniz %100 korunur.',
    },
    {
      question: 'Kendi alan adımı kullanabilir miyim?',
      answer:
        'Evet! <strong>Profesyonel</strong> ve <strong>Kurumsal</strong> paketlerde <code>derneginiz.com</code> gibi özel alan adı bağlayabilirsiniz.',
    },
    {
      question: 'Mobil uygulama var mı?',
      answer:
        'Evet! iOS ve Android uygulamalarımızla üyeleriniz etkinlikleri takip edebilir, bildirim alabilir, ödeme yapabilir. Tüm paketlerde erişim var.',
    },
    {
      question: 'Destek ekibi ne zaman ulaşılabilir?',
      answer:
        '<strong>Başlangıç:</strong> E-posta (24 saat içinde)<br>' +
        '<strong>Profesyonel:</strong> Öncelikli e-posta (6 saat içinde)<br>' +
        '<strong>Kurumsal:</strong> 7/24 telefon + e-posta',
    },
    {
      question: 'İptal edersem ne olur?',
      answer:
        'İptal etmek istediğinizde hesabınız dönem sonunda kapanır. Verilerinizi 30 gün içinde dışa aktarabilirsiniz. <strong>Para iade garantisi</strong> ilk 14 gün geçerlidir.',
    },
  ];

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      ref={ref}
      sx={{
        mt: { xs: 8, md: 10 },
        pt: { xs: 6, md: 8 },
        borderTop: `1px solid ${P.neutral[200]}`,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 6 } }}>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <SectionBadge>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#f97316',
              }}
            />
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#f97316',
                letterSpacing: '0.5px',
              }}
            >
              SSS
            </Typography>
          </SectionBadge>
        </motion.div>

        <motion.div
          variants={fadeUp}
          custom={1}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              fontWeight: 700,
              mb: 2,
              color: P.neutral[800],
              letterSpacing: '-0.02em',
            }}
          >
            Sıkça Sorulan{' '}
            <Box component="span" sx={{ color: '#f97316' }}>Sorular</Box>
          </Typography>
        </motion.div>

        <motion.div
          variants={fadeUp}
          custom={2}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <Typography
            sx={{
              maxWidth: 500,
              mx: 'auto',
              color: P.neutral[500],
              lineHeight: 1.7,
              fontSize: '0.95rem',
            }}
          >
            En çok merak edilen soruların cevapları.
            Daha fazla soru için bizimle iletişime geçin.
          </Typography>
        </motion.div>
      </Box>

      {/* Accordion */}
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            variants={fadeUp}
            custom={index + 3}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            <Accordion
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              sx={{
                mb: 1.5,
                borderRadius: '12px !important',
                boxShadow: 'none',
                border: `1px solid ${P.neutral[200]}`,
                backgroundColor: '#ffffff',
                overflow: 'hidden',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: '0 0 12px 0',
                  borderColor: '#f97316',
                  boxShadow: `0 4px 16px ${alpha('#f97316', 0.1)}`,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <AccordionSummary
                expandIcon={
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: expanded === `panel${index}` 
                        ? '#f97316' 
                        : P.neutral[100],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <ExpandMoreIcon 
                      sx={{ 
                        fontSize: 18, 
                        color: expanded === `panel${index}` ? '#fff' : P.neutral[500],
                        transition: 'transform 0.3s ease',
                      }} 
                    />
                  </Box>
                }
                sx={{
                  px: 2.5,
                  py: 1.5,
                  minHeight: '56px !important',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    margin: '8px 0 !important',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: expanded === `panel${index}` 
                      ? `linear-gradient(135deg, #f97316 0%, ${alpha('#f97316', 0.8)} 100%)`
                      : alpha('#f97316', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <HelpOutlineIcon
                    sx={{
                      fontSize: 18,
                      color: expanded === `panel${index}` ? '#fff' : '#f97316',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: P.neutral[800],
                    fontSize: '0.95rem',
                  }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  px: 2.5,
                  pb: 2.5,
                  pt: 0,
                  backgroundColor: alpha('#f97316', 0.02),
                  borderTop: `1px solid ${P.neutral[100]}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pl: 6 }}>
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 18,
                      color: '#10b981',
                      mt: 0.3,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      color: P.neutral[600],
                      lineHeight: 1.7,
                      fontSize: '0.9rem',
                      '& strong': { color: '#f97316', fontWeight: 600 },
                      '& code': {
                        backgroundColor: alpha('#f97316', 0.1),
                        color: '#f97316',
                        px: 0.8,
                        py: 0.3,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

export default FAQSection;